/**
 * Single shared React Query client. Tuned for Pulse's read-heavy workload:
 *
 * - staleTime 30s — KPIs/cherries don't change every second. If a user
 *   bounces between /home and /inventory, they see cached data instantly
 *   instead of a spinner. The refetch on focus catches anything that did
 *   change while they were away.
 * - retry 1 — most failures are network blips. Retry once, then surface.
 * - refetchOnWindowFocus true — implementer comes back from a phone call,
 *   data auto-refreshes. Feels like a "live" app.
 *
 * Cache keys follow the pattern ["resource", tenant, ...params]. Tenant
 * comes from the auth token implicitly (backend resolves it), but we still
 * key on user_id to invalidate everything on logout.
 */
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,          // 30s — KPIs feel fresh
      gcTime: 5 * 60_000,         // keep in memory 5 min after last use
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Call after logout / token change to wipe every cached query so the
 * incoming tenant never sees the previous one's data. Belt-and-suspenders
 * on top of the backend's tenant isolation.
 */
export function clearAllQueryCache() {
  queryClient.clear();
}
